/**
 * MCP OAuth Service - Integration with ultron-turing MCP OAuth tools
 */

import { logger } from '../utils/logger';

interface OAuthConfig {
  redirectUri: string;
  scope: string;
  state?: string;
}

interface OAuthSession {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  environment?: 'production' | 'staging'; // 环境信息（production/staging）
  apiUrl?: string; // 🆕 后端返回的 API URL（可选，用于覆盖默认 URL）
  userInfo?: {
    email?: string;
    name?: string;
    id?: string;
  };
}

class MCPOAuthService {
  private readonly STORAGE_KEY = 'ultron_tokens';
  private readonly STATE_KEY = 'mcp_oauth_state';

  // Production redirect URI - OAuth will callback to our frontend app
  private readonly REDIRECT_URI = 'https://boards.ultronsmart.com/auth/callback';

  /**
   * Get OAuth base URL based on environment
   */
  private getOAuthBaseUrl(): string {
    // Check if we have a stored session with environment info
    const session = this.getSession();
    if (session?.environment === 'staging') {
      return process.env.REACT_APP_OAUTH_STAGING_URL || 'https://turing-staging.ultronsmart.com';
    }
    // Default to production
    return 'https://turing.ultronsmart.com';
  }

  /**
   * Initiate OAuth login flow - Direct redirect to handle OAuth flow
   * Turing Server handles ALL OAuth complexity and sets httpOnly cookie
   */
  initiateLogin(appId?: string): void {
    // ALWAYS use KLMGeDMy as the app_id for Unified Boards
    const selectedAppId = 'KLMGeDMy';

    // Final return URL after OAuth completes (full URL)
    // Use current origin for development/production flexibility
    const returnUrl = `${window.location.origin}/`;

    // Get OAuth base URL based on environment (if available from previous session)
    const oauthBaseUrl = this.getOAuthBaseUrl();

    // Build OAuth URL - Turing Server handles the entire flow
    // NO redirect_uri needed - Turing Server handles OAuth internally
    const loginUrl = `${oauthBaseUrl}/auth/login?` +
      `app_id=${selectedAppId}&` +
      `return_url=${encodeURIComponent(returnUrl)}`;

    logger.log('🔐 Redirecting to OAuth login:', {
      loginUrl,
      oauthBaseUrl,
      app_id: selectedAppId,
      return_url: returnUrl,
      timestamp: new Date().toISOString()
    });

    // Store the selected appId for later use
    sessionStorage.setItem('selected_app_id', selectedAppId);

    // Direct redirect to OAuth login page
    // Flow: Turing Server handles everything and redirects back to return_url
    window.location.href = loginUrl;
  }

  /**
   * Handle OAuth completion message from popup
   */
  private handleOAuthMessage = (event: MessageEvent): void => {
    // Verify origin
    if (event.origin !== 'https://turing.ultronsmart.com') return;

    if (event.data.type === 'oauth_complete') {
      logger.log('✅ Received OAuth completion message');

      // Close popup
      const popup = (window as any).oauthPopup;
      if (popup && !popup.closed) {
        popup.close();
      }

      // Remove event listener
      window.removeEventListener('message', this.handleOAuthMessage);

      // Reload to dashboard
      window.location.href = '/';
    }
  }

  /**
   * Setup storage event listener for cross-tab OAuth completion
   */
  setupStorageListener(): void {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === 'oauth_status' && e.newValue) {
        try {
          const status = JSON.parse(e.newValue);
          if (status.completed && Date.now() - status.timestamp < 5000) {
            logger.log('✅ OAuth completed in another tab');
            // Clear the status
            localStorage.removeItem('oauth_status');
            // Reload to dashboard
            window.location.href = '/';
          }
        } catch (error) {
          logger.error('Failed to parse oauth_status:', error);
        }
      }
    });
  }

  /**
   * Handle OAuth callback and exchange code for token
   */
  async handleCallback(code: string, state: string): Promise<OAuthSession> {
    // Verify state to prevent CSRF
    const savedState = localStorage.getItem(this.STATE_KEY);
    if (!savedState || state !== savedState) {
      logger.error('State mismatch:', { received: state, saved: savedState });
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    try {
      // Decode state to get sessionId
      let sessionId = '';
      try {
        const stateData = JSON.parse(atob(state));
        sessionId = stateData.sessionId || '';
        logger.log('📋 State decoded:', {
          tenantId: stateData.tenantId,
          hasSessionId: !!sessionId,
          sessionIdPreview: sessionId.substring(0, 10) + '...'
        });
      } catch (e) {
        logger.error('Failed to decode state:', e);
      }

      const requestBody = {
        code: code,
        state: state,
        sessionId: sessionId,
        client_id: (process.env.REACT_APP_MCP_CLIENT_ID || '7KjB9mNpQrXs2vWzA3dFgH').trim(),
        tenant_id: (process.env.REACT_APP_MCP_APP_ID || 'KLMGeDMy').trim()
      };

      logger.log('🔄 Exchanging code for token...', {
        endpoint: 'https://turing.ultronsmart.com/auth/token',
        code: code.substring(0, 10) + '...',
        state: state.substring(0, 10) + '...',
        sessionId: sessionId?.substring(0, 10) + '...',
        requestBody
      });

      const response = await fetch('https://turing.ultronsmart.com/auth/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include' // Include cookies for session
      });

      logger.log('📥 Token response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ Token exchange failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Failed to exchange code for token: ${response.status} ${errorText}`);
      }

      const tokens = await response.json();
      
      // Log token response for debugging (excluding sensitive data)
      logger.log('📥 Token response received:', {
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        hasIdToken: !!tokens.id_token,
        environment: tokens.environment || 'not provided',
        expiresIn: tokens.expires_in
      });

      // Store session
      const session: OAuthSession = {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (tokens.expires_in * 1000),
        environment: tokens.environment || 'production', // 默认为 production
        apiUrl: tokens.api_url || undefined // ✅ FIX: Save backend-provided API URL
      };

      // Get user info if token includes it
      if (tokens.id_token) {
        try {
          const payload = this.parseJWT(tokens.id_token);
          session.userInfo = {
            email: payload.email,
            name: payload.name,
            id: payload.sub
          };
        } catch (e) {
          logger.error('Failed to parse ID token:', e);
        }
      }

      await this.saveSession(session);
      return session;
    } catch (error) {
      logger.error('OAuth callback error:', error);
      throw error;
    } finally {
      // Clean up state
      localStorage.removeItem(this.STATE_KEY);
    }
  }

  /**
   * Check current authentication status using Token-Based OAuth 2.0
   * Tokens are stored in localStorage/sessionStorage (based on keepLoggedIn)
   * Automatically attempts token refresh if expired
   */
  async checkAuthStatus(): Promise<{ authenticated: boolean; session?: any }> {
    try {
      const session = this.getSession();

      if (!session || !session.accessToken) {
        logger.log('⚠️ No OAuth access token found');
        return { authenticated: false };
      }

      // Check if token is expired
      if (session.expiresAt && Date.now() > session.expiresAt) {
        logger.log('⚠️ OAuth token expired, attempting refresh...');

        // Attempt to refresh token
        try {
          const { authService } = await import('./authService');
          const refreshSuccess = await authService.refreshToken();

          if (refreshSuccess) {
            logger.log('✅ Token refreshed successfully');
            const refreshedSession = this.getSession();
            return { authenticated: true, session: refreshedSession };
          } else {
            // ✅ Don't logout here! Just return unauthenticated
            // Let caller decide what to do (show login prompt, retry, etc.)
            logger.warn('⚠️ Token refresh failed - returning unauthenticated (NOT logging out)');
            return { authenticated: false };
          }
        } catch (error) {
          // ✅ Don't logout on network errors or temporary failures
          // Only return unauthenticated, let caller handle UI
          logger.warn('⚠️ Token refresh failed with error - returning unauthenticated (NOT logging out):', error);
          return { authenticated: false };
        }
      }

      logger.log('✅ Authenticated via Token-Based OAuth', {
        authenticated: true,
        hasAccessToken: true,
        hasRefreshToken: !!session.refreshToken,
        expiresAt: session.expiresAt ? new Date(session.expiresAt).toISOString() : 'unknown'
      });

      return { authenticated: true, session };
    } catch (error) {
      logger.error('Failed to check auth status:', error);
      return { authenticated: false };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<OAuthSession> {
    try {
      // Get OAuth base URL based on current environment
      const oauthBaseUrl = this.getOAuthBaseUrl();
      const tokenUrl = `${oauthBaseUrl}/oauth/token`;
      
      logger.log('🔄 Refreshing token:', { tokenUrl, environment: this.getSession()?.environment || 'production' });
      
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: (process.env.REACT_APP_MCP_CLIENT_ID || '').trim(),
          client_secret: process.env.REACT_APP_MCP_CLIENT_SECRET || ''
        }),
        credentials: 'include' // Include cookies for session
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const tokens = await response.json();

      // Preserve user info and environment from previous session
      const oldSession = this.getSession();
      
      const session: OAuthSession = {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token || refreshToken,
        expiresAt: Date.now() + (tokens.expires_in * 1000),
        // 优先使用新 token 的 environment，否则保留旧的
        environment: tokens.environment || oldSession?.environment || 'production'
      };

      if (oldSession?.userInfo) {
        session.userInfo = oldSession.userInfo;
      }

      await this.saveSession(session);
      return session;
    } catch (error) {
      logger.error('Failed to refresh token:', error);
      throw error;
    }
  }

  /**
   * Logout and clear session (clears from both localStorage and sessionStorage)
   */
  logout(): void {
    // Clear from both storages
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STATE_KEY);
    sessionStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(this.STATE_KEY);
    sessionStorage.removeItem('auth_mode');

    // Clear any API credentials as well
    localStorage.removeItem('ultron-energy-credentials');

    logger.log('🔐 Logged out, cleared tokens from both storages');
  }

  /**
   * Get current session (checks both localStorage and sessionStorage)
   */
  getSession(): OAuthSession | null {
    // Try localStorage first (for keepLoggedIn = true)
    let stored = localStorage.getItem(this.STORAGE_KEY);
    let storageType = 'localStorage';

    // Fallback to sessionStorage
    if (!stored) {
      stored = sessionStorage.getItem(this.STORAGE_KEY);
      storageType = 'sessionStorage';
    }

    if (!stored) return null;

    try {
      const session = JSON.parse(stored);
      logger.log(`🔐 Session loaded from ${storageType}`);
      return session;
    } catch (error) {
      logger.error('Failed to parse stored session:', error);
      return null;
    }
  }

  /**
   * Save session to dynamic storage (localStorage or sessionStorage based on keepLoggedIn)
   */
  private async saveSession(session: OAuthSession): Promise<void> {
    try {
      const { getUserPreferences } = await import('./userPreferencesApi');
      const preferences = await getUserPreferences();
      const keepLoggedIn = preferences.dashboardSettings?.keepLoggedIn || false;

      // ✅ Update authService cache to prevent circular dependency
      const { authService } = await import('./authService');
      authService.updateKeepLoggedInCache(keepLoggedIn);

      const storage = keepLoggedIn ? localStorage : sessionStorage;
      storage.setItem(this.STORAGE_KEY, JSON.stringify(session));
      sessionStorage.setItem('auth_mode', 'oauth');

      logger.log(`🔐 Session saved to ${keepLoggedIn ? 'localStorage' : 'sessionStorage'} (keepLoggedIn: ${keepLoggedIn})`);
    } catch (error) {
      // Fallback to sessionStorage if cannot determine keepLoggedIn
      logger.error('Failed to get keepLoggedIn preference, defaulting to sessionStorage:', error);
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(session));
      sessionStorage.setItem('auth_mode', 'oauth');
    }
  }

  /**
   * Generate random state for CSRF protection
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Parse JWT token
   */
  private parseJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      logger.error('Failed to parse JWT:', error);
      return {};
    }
  }

  /**
   * Generate PKCE code challenge
   */
  private async generateCodeChallenge(): Promise<string> {
    // Generate a random code verifier
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const verifier = this.base64URLEncode(array);

    // Store verifier for later use
    sessionStorage.setItem('pkce_verifier', verifier);

    // Generate SHA256 hash of the verifier
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);

    // Convert to base64url
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Base64 URL encode
   */
  private base64URLEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(buffer)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Get authorization header for API calls
   * Returns Bearer token from OAuth session
   */
  getAuthHeader(): { Authorization: string } | {} {
    const session = this.getSession();
    if (!session?.accessToken) {
      logger.warn('⚠️ No access token available for API calls');
      return {};
    }

    return {
      Authorization: `Bearer ${session.accessToken}`
    };
  }

  /**
   * Check if we should use OAuth or API key authentication
   */
  useOAuth(): boolean {
    return sessionStorage.getItem('auth_mode') === 'oauth';
  }
}

export const mcpOAuthService = new MCPOAuthService();